import { computed, defineComponent, onMounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { ProjectGetter } from '@/store/project/project.module';
import { Project }  from '@/store/project/project.model';
import { ProcureAction, ProcureGetter } from '@/store/procure/procure.module';
import { Procure } from '@/store/procure/procure.model';
import { ProvideTypeAction, ProvideTypeGetter } from '@/store/provideType/provideType.module';
import { ProvideType } from '@/store/provideType/provideType.model';
import { ProcureSchema } from '@/store/procure/procure.validator';
import Datepicker from '@vuepic/vue-datepicker';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'ProjectProcureFormComponent',
  components: {
    Datepicker,
    BaseModalComponent,
  },
  props: {
    projectId: {
      type: Number,
    },
  },
  emits: [
    'refresh'
  ],
  setup: (props, context) => {
    const store = useStore();
    const procureForm = ref<InstanceType<typeof BaseModalComponent>>();
    const project = computed<Project>(() => store.getters[ProjectGetter.BY_ID](props.projectId));
    const procure = computed<Procure | null>(() => store.getters[ProcureGetter.GET]);
    const provideTypes = computed<ProvideType[]>(() => store.getters[ProvideTypeGetter.ALL]);
    const isEditMode = computed(() => procure.value != null);
    const title = computed(() => isEditMode.value ? 'แก้ไขข้อมูลการจัดซื้อ/จัดหา/จัดจ้าง' : 'เพิ่มข้อมูลการจัดซื้อ/จัดหา/จัดจ้าง');
    const hideEndDate = ref(false);

    // form
    interface Form {
      projectId?: number | null;
      provideTypeId?: number | null;
      receiveBudgetDate?: Date | null;
      projectOwner?: string | null;
      location?: string | null;
      realBudget?: number | null;
      startDate?: Date | null;
      remark?: string | null;
      endDate?: Date | null;
    }
    const formData = reactive<Form>({
      projectId: null,
      provideTypeId: null,
      receiveBudgetDate: null,
      projectOwner: '',
      location: '',
      realBudget: null,
      startDate: null,
      remark: null,
      endDate: null,
    });
    const validator = computed(() => ProcureSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      projectId: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'projectId')),
      provideTypeId: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'provideTypeId')),
      receiveBudgetDate: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'receiveBudgetDate')),
      projectOwner: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'projectOwner')),
      location: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'location')),
      realBudget: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'realBudget')),
      startDate: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'startDate')),
      endDate: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'endDate')),
    });
    const isValid = computed(() => !errors.projectId &&
      !errors.provideTypeId &&
      !errors.receiveBudgetDate &&
      !errors.projectOwner &&
      !errors.location &&
      !errors.realBudget &&
      !errors.startDate &&
      (!errors.endDate || isEditMode.value));
    const isLoading = computed<boolean>(() => store.getters[ProcureGetter.STATUS] === 'init' || store.getters[ProcureGetter.STATUS] === 'loading');
    const submited = ref(false);

    onMounted(() => {
      store.dispatch(ProvideTypeAction.FETCH);
    });

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        formData.projectId = props.projectId;
        if (isEditMode.value) {
          formData.provideTypeId = procure.value?.provideType.id;
          formData.receiveBudgetDate = procure.value?.receiveBudgetDate;
          formData.projectOwner = procure.value?.projectOwner;
          formData.location = procure.value?.location;
          formData.realBudget = procure.value?.realBudget;
          formData.startDate = procure.value?.startDate;
          formData.remark = procure.value?.remark;
        }
        resolve();
      })
      .then(() => procureForm.value?.show());
    };

    // hide modal
    const hide = () => {
      procureForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.projectId = null;
        formData.provideTypeId = null;
        formData.receiveBudgetDate = null;
        formData.projectOwner = '',
        formData.location = '',
        formData.realBudget = null;
        formData.startDate = null;
        formData.remark = null;
        formData.endDate = null;
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(ProcureAction.UPDATE, {
            procureId: formData.projectId,
            provideTypeId: formData.provideTypeId,
            receiveBudgetDate: formData.receiveBudgetDate,
            procureOwner: formData.projectOwner,
            location: formData.location,
            realBudget: formData.realBudget,
            startDate: formData.startDate,
            remark: formData.remark,
          }).then(() => hide());
        } else {
          store.dispatch(ProcureAction.CREATE, {
            procureId: formData.projectId,
            provideTypeId: formData.provideTypeId,
            receiveBudgetDate: formData.receiveBudgetDate,
            procureOwner: formData.projectOwner,
            location: formData.location,
            realBudget: formData.realBudget,
            startDate: formData.startDate,
            endDate: formData.endDate,
            remark: formData.remark,
          }).then(() => hide());
        }
      }
    };

    const refresh = () => {
      if (project.value?.projectStatus.key >= 1) {
        context.emit('refresh');
      }
    };

    const projectTypeChnage = () => {
      formData.endDate = (formData.provideTypeId === 13)
        ? new Date('1901-01-01T17:00:00.000Z')
        : null;
      hideEndDate.value = formData.provideTypeId === 13;

      unsubmit();
    };

    return {
      title,
      project,
      provideTypes,
      formData,
      procureForm,
      errors,
      submited,
      isEditMode,
      isLoading,
      hideEndDate,
      show,
      hide,
      unsubmit,
      clearForm,
      submitClick,
      projectTypeChnage,
      refresh,
    };
  },
});
